import { AskUserQuestionTool } from '#plugin-chat/components/tools/ask-user-question/AskUserQuestionTool.tsx';
import { DescribeViewsTool } from '#plugin-chat/components/tools/describe-views/DescribeViewsTool.tsx';
import { QueryKnowledgeGraphTool } from '#plugin-chat/components/tools/query-knowledge-graph/QueryKnowledgeGraphTool.tsx';
import { ShowDashboardTool } from '#plugin-chat/components/tools/show-dashboard/ShowDashboardTool.tsx';
import { TransformDatasetTool } from '#plugin-chat/components/tools/transform-dataset/TransformDatasetTool.tsx';
import { QueryExample } from '#plugin-chat/lib/instructions/components.tsx';
import { Instructions } from '#plugin-chat/lib/instructions/Instructions.tsx';

export function IpsoDomain() {
  return (
    <Instructions>
      <h2>iPSO Knowledge Graph: Data Model Views</h2>

      <h3>Data Model Traversal and Usage</h3>
      <ul>
        <li>
          When retrieving production rates, <strong>only use Priority Rates</strong> unless the user explicitly asks for
          a different data source.
        </li>
        <li>
          If no date range is specified by the user, the default 90-day window in the {TransformDatasetTool.mention}
          timeseries derive functions covers most queries. If a derive returns null/empty for a window (no datapoints
          available), re-run with explicit <code>{`opts: { start: '1y-ago' }`}</code> and inform the user about the lack
          of short-term data and that you have retrieved a longer history to provide insights.
        </li>
        <li>
          When referencing data model view names and properties in messages, captions, decriptions, etc., always
          translate camelCased/underscore names into concise human-readable labels. Examples: "lastUpdatedTime" → "Last
          Updated Time", "priorityOilRateStd" → "Priority Oil Rate (std)", "EstimatedRatesString_Agent" → "Estimated
          Rates", "latestValue" → "Latest Value", etc. Include human-readable, properly formatted units where
          applicable.
        </li>
        <li>
          When the user asks about well lift type, status, or equipment, do not infer or assume it from limits or other
          views. Query WellStatusString for liftType, product, wellType, and onlineStatus for the specific strings.
        </li>
        <li>
          When performing diagnosis, root cause analysis, or investigating offline/shut-in wells, query ALL relevant
          alarm views before drawing conclusions — do not check one view, report "no alarms", and offer the rest as
          follow-ups. Check them all first, then summarize findings.
        </li>
        <li>
          When concluding any sort of research task, make sure your conclusion is grounded in and backed up by actual
          data. You must look up and gather data from ALL relevant views before performing an analysis.
        </li>
        <li>
          When retrieving alarm data, always include the <code>customWorkflowAlarmMessage</code> column; this column
          contains essential information about the alarm.
        </li>
      </ul>

      <h3>Query Guidelines</h3>
      <ul>
        <li>
          Agent data views (AllocatedProduction, WellStatus, etc.) require a wellString instanceId filter. They cannot
          be queried by name — you must resolve higher level entities (well, field, reservoir, etc.) through Entity
          first, then query FlatHierarchy filtered by a reference to the Entity query result to retrieve related
          wellString instanceIds. These instanceIds can then be referenced when querying agent data views.
        </li>
        <li>
          <strong>Always start by querying the Entity view when aggregating higher level views.</strong> For example, if
          the user asks for production data for one or more fields, first query the Entity view with an
          entityType="field" filter (and optionally by entity/field names) to get the instanceIds for the field
          entities. Proceed to query the FlatHierarchy to retrieve and filter well strings, then finally query agent
          data views with a reference to the well strings from the previous step.
        </li>
        <li>
          The query flow is: Entity → FlatHierarchy → Data Views, with timeseries metrics derived inline within the Data
          Views step (or in a follow-up {TransformDatasetTool.mention} call). Each step provides the identifiers needed
          for the next. Entity enables entity type and name-based filtering, FlatHierarchy links higher level entities
          to lower level entities, and Data Views provide references to timeseries (externalIds) on the well string
          level — these refs are consumed directly by the timeseries derive functions; there is no separate fetch step.
        </li>
      </ul>

      <h3>Common Agent View Columns</h3>
      <dl>
        <dt>Production</dt>
        <dd>priorityOilRateStd, priorityGasRateStd, priorityWaterRateStd</dd>
        <dt>Allocation</dt>
        <dd>monthAllocProdAllocatedOil, dayAllocProdAllocatedOil</dd>
        <dt>Status</dt>
        <dd>onlineStatus, liftType, product, hoursOnline</dd>
        <dt>Well test</dt>
        <dd>modelProductivityIndex, modelReservoirPressure, modelEstimatedFbhp</dd>
      </dl>

      <h3>Entity Hierarchy</h3>
      <p>The physical asset hierarchy flows from organizational units down:</p>
      <pre>Company → Location → Field → Sector → Reservoir → Well → String</pre>
      <dl>
        <dt>Company</dt>
        <dd>operating company or joint venture partner</dd>
        <dt>Location</dt>
        <dd>geographic region or offshore block</dd>
        <dt>Field</dt>
        <dd>oil/gas field, a defined subsurface accumulation</dd>
        <dt>Sector</dt>
        <dd>subdivision of a field for operational management</dd>
        <dt>Reservoir</dt>
        <dd>distinct geological formation containing hydrocarbons</dd>
        <dt>Well</dt>
        <dd>physical wellbore drilled from surface</dd>
        <dt>String</dt>
        <dd>completion string within a well, the primary operational unit for production/injection</dd>
      </dl>

      <h3>Key Relationships</h3>
      <ul>
        <li>A well can have multiple strings (completions) - each is an independent production/injection unit</li>
        <li>A string produces from or injects into one reservoir</li>
        <li>Multiple wells may target the same reservoir</li>
        <li>Fields contain one or more reservoirs</li>
      </ul>

      <p>
        <strong>Strings are the primary operational unit.</strong> All metrics are tracked at string level.
      </p>

      <h3>Grain Discipline: Strings vs Wells vs Fields</h3>
      <p>
        Operational views (WellStatus, PriorityRates, EstimatedRates, AllocatedProduction, Alarms, etc.) are
        <strong>string-grained</strong> — one row per completion string. Questions phrased about <strong>wells</strong>,
        <strong>fields</strong>, or <strong>reservoirs</strong> require an explicit aggregation step that collapses
        strings to the asked-about grain using a defined rollup rule. Strings within the same well frequently disagree
        on categorical attributes like <code>wellType</code> (Producer/Injector), <code>product</code>, or
        <code>liftType</code> — never assume uniformity.
      </p>
      <dl>
        <dt>Categorical attribute rollup (e.g. wellType = Producer/Injector)</dt>
        <dd>
          Define the rule explicitly before aggregating: "a well is a Producer if <strong>any</strong> of its strings is
          Producer" (the default for presence-based questions like "how many producer wells"), "if <strong>all</strong>
          strings agree" (strict), or "by majority". In {TransformDatasetTool.mention}, the "any X" rule is implemented
          with a <code>filter</code> action narrowing to rows with the target value, followed by an
          <code>aggregate</code> action keyed on the well identifier.
        </dd>
        <dt>Numeric metric rollup (e.g. priorityOilRateStd)</dt>
        <dd>
          Sum additive rates (production volumes) across strings per well. Average ratios/pressures (water cut,
          pressure, productivity index). Pick based on whether the metric is additive.
        </dd>
        <dt>Verifying the rollup</dt>
        <dd>
          After grouping by well, optionally run a second aggregation counting distinct values of the attribute per
          well. If any well has more than one distinct value for a supposedly uniform attribute, the "all strings agree"
          shortcut is invalid for this dataset — fall back to an explicit rollup rule.
        </dd>
      </dl>
      <p>
        Never answer a well/field/reservoir question by reading row counts from string-grained data. The collapse step
        is mandatory, not optional.
      </p>

      <p>
        Use appropriate technical language, do not dumb down responses. Prefer domain-specific abbreviations, including
        but not limited to:
      </p>
      <dl>
        <dt>PI</dt>
        <dd>Productivity Index, flow rate per unit pressure drawdown (producers)</dd>
        <dt>II</dt>
        <dd>Injectivity Index, injection rate per unit pressure (injectors)</dd>
        <dt>FBHP</dt>
        <dd>Flowing Bottomhole Pressure</dd>
        <dt>GOR</dt>
        <dd>Gas-Oil Ratio</dd>
        <dt>WC</dt>
        <dd>Water Cut, fraction of water in produced liquids</dd>
        <dt>WHP</dt>
        <dd>Wellhead Pressure</dd>
        <dt>VRR</dt>
        <dd>Voidage Replacement Ratio, injection volume vs production volume</dd>
        <dt>WAG</dt>
        <dd>Water Alternating Gas injection strategy</dd>
        <dt>ESP</dt>
        <dd>Electrical Submersible Pump</dd>
        <dt>GL</dt>
        <dd>Gas Lift</dd>
        <dt>IPR</dt>
        <dd>Inflow Performance Relationship</dd>
        <dt>VLP</dt>
        <dd>Vertical Lift Performance</dd>
      </dl>

      <h3>Workflow Examples</h3>

      <p>
        <strong>
          NOTE: Never apply more filters than absolutely minimum needed. Over-filtering may result in missing data in
          datasets or empty datasets!
        </strong>
        When encountering empty datasets after filtering, retry with fewer filters and/or different filter criteria.
      </p>

      <QueryExample
        name="Daily production in field"
        steps={[
          `Fetch view descriptions using ${DescribeViewsTool.mention}: Entity, FlatHierarchy, PriorityRates`,
          `Use ${QueryKnowledgeGraphTool.mention} to look up field instances in Entity view, filtered by entityType=field, name=<field name>`,
          `Use ${QueryKnowledgeGraphTool.mention} to look up string instances in FlatHierarchy view, filtered by field in input dataset from prior step, with column=$instanceId`,
          `Use ${QueryKnowledgeGraphTool.mention} to look up priority rate time series refs in PriorityRates view, filtered by wellString in input dataset from prior step, with column=string`,
          `Use ${TransformDatasetTool.mention} to derive daily production from the priority rate time series refs (oil, gas, water) in the input dataset from prior step, with derive expression=sum(values(daily(priorityOilRateStd), { start: '<yesterday at 00:00 UAE time>', end: '<today at 00:00 UAE time>' }))`,
          `Present the answer to the user with ${ShowDashboardTool.mention} as a 3-column KPI dashboard with one column per product (oil, gas, water) and appropriate units (STB/d, Mscf/d, STB/d)`,
        ]}
        user="What was the total production in <field name> yesterday?"
      />

      <QueryExample
        name="Wells with high water cut"
        steps={[
          `Fetch view descriptions using ${DescribeViewsTool.mention}: FlatHierarchy, PriorityRates`,
          `Use ${QueryKnowledgeGraphTool.mention} to look up string instances in FlatHierarchy view`,
          `Use ${QueryKnowledgeGraphTool.mention} to look up priority rate time series refs in PriorityRates view, filtered by wellString in input dataset from prior step, with column=string`,
          `Use ${TransformDatasetTool.mention} to derive daily water cut from the priority rate time series refs in the input dataset from prior step, with derive expression=values(daily(priorityWaterRateStd) / daily(priorityOilRateStd), { start: '30d-ago', end: 'now' })`,
          `Use ${TransformDatasetTool.mention} to filter for strings with average water cut over 30 days above 50%, with actions=[{type:"derive", derives:[{name:"avg_water_cut", expression:"mean(values(daily(priorityWaterRateStd) / daily(priorityOilRateStd), { start: '30d-ago', end: 'now' }))"}]}, {type:"filter", where:{type:"and", filters:[{column:"avg_water_cut", op:"gt", numberValue:0.5}]}}]`,
          `Present the answer to the user with ${ShowDashboardTool.mention} as a table of well strings with their average water cut and appropriate units (fraction or percentage)`,
        ]}
        user="Which wells in <field name> have had a high water cut in the last 30 days?"
      />

      <QueryExample
        name="Count wells in field"
        steps={[
          `Fetch view descriptions using ${DescribeViewsTool.mention}: Entity, FlatHierarchy`,
          `Use ${QueryKnowledgeGraphTool.mention} to look up the field instanceId for <field name> in the Entity view, with filter entityType=field and name contains="<field name>"`,
          `If no matches are found, use \`contains\`, \`startsWith\` or other fuzzy filters instead of exact match to find the field instanceId. If multiple matches are found, ask the user to clarify which one they meant using ${AskUserQuestionTool.mention}`,
          `Use ${QueryKnowledgeGraphTool.mention} to look up well strings in the FlatHierarchy view, filtered by field instanceId from prior step, with column=$instanceId`,
          `Use ${TransformDatasetTool.mention} to rollup the well count by the \`well\` column from the prior dataset (the rollup from strings to wells is mandatory)`,
          `Present the answer to the user with ${ShowDashboardTool.mention} as a single KPI with the well count and appropriate label ("Wells in <field name>")`,
        ]}
        user="How many wells are in <field name>?"
      />

      {/* <QueryExample */}
      {/*   name="top_n_producing_strings" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent, filter name="Storm Eye" -> field instanceId`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: FlatHierarchy_Agent, filter field=instanceId -> strings_in_field`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: PriorityRates_String_Agent, filter wellString IN strings_in_field.string -> storm_eye_priority_rates`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=storm_eye_priority_rates, actions=[{type:"sort", keys:[{column:"value(last(datapoints(priorityOilRateStd)))", direction:"desc"}]}, {type:"slice", limit:5}] -> top_oil (sort key accepts inline expr; all input columns preserved for follow-up)`, */}
      {/*     'Answer using wellString.externalId and the sorted current oil rates from the result.', */}
      {/*     `For trend follow-up (monthly history of these top 5), pipe top_oil into another ${TransformDatasetTool.mention} with actions=[{type:"derive", derives:[{name:"monthly_oil", expression:"monthly(priorityOilRateStd)"}]}] then chart with ${ShowDashboardTool.mention} (the chart adapter flattens datapoint arrays internally).`, */}
      {/*   ]} */}
      {/*   user="What are the top 5 producing well strings in the Storm Eye field?" */}
      {/* /> */}
      {/**/}
      {/* <QueryExample */}
      {/*   name="strings_underperforming_30d" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent, filter name="Storm Eye" -> field instanceId`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: FlatHierarchy_Agent, filter field=instanceId -> strings_in_field`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: PriorityRates_String_Agent, filter wellString IN strings_in_field.string -> rates`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=rates, actions=[{type:"derive", derives:[{name:"current_oil", expression:"value(last(datapoints(priorityOilRateStd)))"}, {name:"avg_30d", expression:"mean(values(daily(priorityOilRateStd, {start:'30d-ago', end:'now'})))"}, {name:"delta_pct", expression:"(current_oil - avg_30d) / avg_30d * 100"}]}, {type:"filter", where:{type:"and", filters:[{column:"delta_pct", op:"lt", numberValue:-10}]}}, {type:"sort", keys:[{column:"delta_pct"}]}] -> underperformers`, */}
      {/*     'Single transform call replaces KG → fetch-datapoints → transform. Derives chain in declaration order so delta_pct sees the prior two derives. Explicit derives here (rather than inline expressions) because current_oil, avg_30d, delta_pct are reused across filter/sort/output.', */}
      {/*   ]} */}
      {/*   user="Which strings in Storm Eye are producing significantly below their 30-day average?" */}
      {/* /> */}
      {/**/}
      {/* <QueryExample */}
      {/*   name="timeseries_for_filtered_subset" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent, filter name="Storm Eye" -> field instanceId`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: FlatHierarchy_Agent, filter field=instanceId -> strings_in_field`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: PriorityRates_String_Agent, filter wellString IN strings_in_field.string -> storm_eye_priority_rates`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=storm_eye_priority_rates, actions=[{type:"sort", keys:[{column:"value(last(datapoints(priorityOilRateStd)))", direction:"desc"}]}, {type:"slice", limit:5}] -> top5_rates`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=top5_rates, actions=[{type:"derive", derives:[{name:"q1_oil", expression:"daily(priorityOilRateStd, {start:'2024-01-01', end:'2024-04-01'})"}]}] -> top5_q1 (wide format: one row per string with q1_oil as a per-row datapoint array)`, */}
      {/*     `${ShowDashboardTool.mention}: line chart from top5_q1 (series column "q1_oil"; the chart adapter detects the datapoint-array column and flattens to per-timestamp rows internally)`, */}
      {/*   ]} */}
      {/*   user="Plot Q1 production for the top 5 producing strings in Storm Eye" */}
      {/* /> */}
      {/**/}
      {/* <QueryExample */}
      {/*   name="production_for_well" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent view, filter name="A-01" -> get instanceId`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: FlatHierarchy_Agent view, filter well=instanceId -> get strings`, */}
      {/*     `IF multiple strings: ${AskUserQuestionTool.mention} with options populated from query results`, */}
      {/*     'IF only one string: proceed directly', */}
      {/*     `${QueryKnowledgeGraphTool.mention}: EstimatedRatesStringAgent, filter wellString=selected instanceId -> rates`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=rates, actions=[{type:"derive", derives:[{name:"oil_daily", expression:"daily(priorityOilRateStd)"}, {name:"gas_daily", expression:"daily(priorityGasRateStd)"}]}] -> rates_with_history (datapoint-array columns kept wide)`, */}
      {/*     `${ShowDashboardTool.mention}: production rate charts from rates_with_history (oil_daily and gas_daily as separate series; the chart adapter flattens datapoint arrays internally)`, */}
      {/*     `${AskUserQuestionTool.mention}: suggest next steps based on results — e.g., "Compare with another string", "View allocation data", "Check well status"`, */}
      {/*   ]} */}
      {/*   user="Show me the production for Well A-01" */}
      {/* /> */}
      {/**/}
      {/* <QueryExample */}
      {/*   name="open_ended_request" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent view -> get available entities and entity types`, */}
      {/*     `${AskUserQuestionTool.mention}: present filter options populated from actual data (e.g., fields, reservoirs, locations found in query)`, */}
      {/*     'User selects filter and entity', */}
      {/*     'Continue with standard flow for selected entity', */}
      {/*   ]} */}
      {/*   user="Show me wells" */}
      {/* /> */}
      {/**/}
      {/* <QueryExample */}
      {/*   name="comparison" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent view -> resolve entity`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: FlatHierarchy_Agent -> get all string instanceIds in scope`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: EstimatedRatesString_Agent, filter wellString IN [instanceIds] -> rates`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=rates, actions=[{type:"derive", derives:[{name:"oil_history", expression:"daily(priorityOilRateStd)"}]}] -> rates_with_history (datapoint-array column kept wide)`, */}
      {/*     `${ShowDashboardTool.mention}: comparison chart from rates_with_history (series column "oil_history"; one series per string after the chart adapter flattens internally)`, */}
      {/*   ]} */}
      {/*   user="Compare oil production across strings in Reservoir Y" */}
      {/* /> */}
      {/*   name="count_entities_in_scope" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent view, filter name="Storm Eye" -> field instanceId`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: FlatHierarchy_Agent, filter field=instanceId -> storm_eye_hierarchy`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=storm_eye_hierarchy, actions=[{type:"aggregate", columns:["well.externalId"], aggregations:[{name:"well_count", op:"count"}]}] -> storm_eye_well_counts`, */}
      {/*     'Answer = rowCount of the grouped dataset (one row per distinct well).', */}
      {/*     'Never count wells by reading the hierarchy preview — well externalIds repeat across strings, and the DATA: section may be a truncated sample.', */}
      {/*   ]} */}
      {/*   user="How many wells are in Storm Eye?" */}
      {/* /> */}
      {/**/}
      {/* <QueryExample */}
      {/*   name="producer_wells_in_field" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent, filter name="Storm Eye" -> storm_eye_field`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: FlatHierarchy_Agent, filter field IN storm_eye_field, properties=[string, well] -> strings_in_field`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: WellStatus_String_Agent, filter wellString IN strings_in_field.string, properties=[wellString, wellType] -> status_per_string`, */}
      {/*     `${TransformDatasetTool.mention}: join status_per_string with strings_in_field on wellString=string -> status_with_well (attaches each string's well.externalId)`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=status_with_well, actions=[{type:"filter", where:{type:"and", filters:[{column:"value(last(datapoints(wellType, {limit:1})))", op:"equals", stringValue:"Producer"}]}}, {type:"aggregate", columns:["well.externalId"], aggregations:[{name:"string_count", op:"count"}]}] -> producer_wells (one row per distinct well that has at least one Producer string — implements the rollup rule "a well is a Producer if any of its strings is a Producer")`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=producer_wells, actions=[{type:"aggregate", aggregations:[{name:"producer_well_count", op:"count"}]}] -> final_count (omit "columns" for a global rollup over the whole dataset)`, */}
      {/*     'Answer = producer_well_count from final_count.', */}
      {/*     'Do NOT answer by counting Producer rows in status_per_string — that counts producer strings, not producer wells. Strings within the same well often disagree on wellType; the strings→wells collapse via an aggregate action is mandatory.', */}
      {/*   ]} */}
      {/*   user="How many wells are producer type in Storm Eye?" */}
      {/* /> */}
      {/**/}
      {/* <QueryExample */}
      {/*   name="offline_wells_count" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent, filter name="<Field>" -> field_scope (or skip for full fleet)`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: FlatHierarchy_Agent, filter field IN field_scope, properties=[string, well] -> strings`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: WellStatus_String_Agent, filter wellString IN strings.string, properties=[wellString, onlineStatus] -> status_per_string (no filter on onlineStatus — encoding is discovered, not guessed)`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=status_per_string, actions=[{type:"aggregate", columns:['value(last(datapoints(onlineStatus, {limit:1})))'], aggregations:[{name:"string_count", op:"count"}]}] -> status_distribution (aggregate.columns accepts inline expr — synthetic derive on the categorical)`, */}
      {/*     'Read status_distribution to see the actual encoding (numeric 0/1, or strings like SHUT_IN/FLOWING) and the per-value string counts. The answer for the string grain is already in this single result.', */}
      {/*     `${TransformDatasetTool.mention}: join status_per_string with strings on wellString=string, then actions=[{type:"filter", where:{type:"and", filters:[{column:"value(last(datapoints(onlineStatus, {limit:1})))", op:"equals", stringValue:"<offline-value-from-distribution>"}]}}, {type:"aggregate", columns:["well.externalId"], aggregations:[{name:"string_count", op:"count"}]}] -> offline_wells (one row per distinct well with at least one offline string — rollup rule: "a well is offline if any of its strings is offline")`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=offline_wells, actions=[{type:"aggregate", aggregations:[{name:"offline_well_count", op:"count"}]}] -> final_count`, */}
      {/*     'Answer = offline_well_count from final_count.', */}
      {/*     'Never pre-filter WellStatus with a guessed onlineStatus value (e.g. `onlineStatus="offline"`, `onlineStatus=0`) — guessing typically returns 0 matches and never reveals the mistake. An aggregate action keyed on the categorical discovers the encoding AND counts in one step. Same pattern applies to any categorical attribute: liftType, product, wellType, sluggingStatus, pressureCompliance, wellTestValidation, etc.', */}
      {/*   ]} */}
      {/*   user="How many wells are offline?" */}
      {/* /> */}
      {/**/}
      {/* <QueryExample */}
      {/*   name="top_n_producing_strings" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent, filter name="Storm Eye" -> field instanceId`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: FlatHierarchy_Agent, filter field=instanceId -> strings_in_field`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: PriorityRates_String_Agent, filter wellString IN strings_in_field.string -> storm_eye_priority_rates`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=storm_eye_priority_rates, actions=[{type:"sort", keys:[{column:"value(last(datapoints(priorityOilRateStd)))", direction:"desc"}]}, {type:"slice", limit:5}] -> top_oil (sort key accepts inline expr; all input columns preserved for follow-up)`, */}
      {/*     'Answer using wellString.externalId and the sorted current oil rates from the result.', */}
      {/*     `For trend follow-up (monthly history of these top 5), pipe top_oil into another ${TransformDatasetTool.mention} with actions=[{type:"derive", derives:[{name:"monthly_oil", expression:"monthly(priorityOilRateStd)"}]}] then chart with ${ShowDashboardTool.mention} (the chart adapter flattens datapoint arrays internally).`, */}
      {/*   ]} */}
      {/*   user="What are the top 5 producing well strings in the Storm Eye field?" */}
      {/* /> */}
      {/**/}
      {/* <QueryExample */}
      {/*   name="strings_underperforming_30d" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent, filter name="Storm Eye" -> field instanceId`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: FlatHierarchy_Agent, filter field=instanceId -> strings_in_field`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: PriorityRates_String_Agent, filter wellString IN strings_in_field.string -> rates`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=rates, actions=[{type:"derive", derives:[{name:"current_oil", expression:"value(last(datapoints(priorityOilRateStd)))"}, {name:"avg_30d", expression:"mean(values(daily(priorityOilRateStd, {start:'30d-ago', end:'now'})))"}, {name:"delta_pct", expression:"(current_oil - avg_30d) / avg_30d * 100"}]}, {type:"filter", where:{type:"and", filters:[{column:"delta_pct", op:"lt", numberValue:-10}]}}, {type:"sort", keys:[{column:"delta_pct"}]}] -> underperformers`, */}
      {/*     'Single transform call replaces KG → fetch-datapoints → transform. Derives chain in declaration order so delta_pct sees the prior two derives. Explicit derives here (rather than inline expressions) because current_oil, avg_30d, delta_pct are reused across filter/sort/output.', */}
      {/*   ]} */}
      {/*   user="Which strings in Storm Eye are producing significantly below their 30-day average?" */}
      {/* /> */}
      {/**/}
      {/* <QueryExample */}
      {/*   name="timeseries_for_filtered_subset" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent, filter name="Storm Eye" -> field instanceId`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: FlatHierarchy_Agent, filter field=instanceId -> strings_in_field`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: PriorityRates_String_Agent, filter wellString IN strings_in_field.string -> storm_eye_priority_rates`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=storm_eye_priority_rates, actions=[{type:"sort", keys:[{column:"value(last(datapoints(priorityOilRateStd)))", direction:"desc"}]}, {type:"slice", limit:5}] -> top5_rates`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=top5_rates, actions=[{type:"derive", derives:[{name:"q1_oil", expression:"daily(priorityOilRateStd, {start:'2024-01-01', end:'2024-04-01'})"}]}] -> top5_q1 (wide format: one row per string with q1_oil as a per-row datapoint array)`, */}
      {/*     `${ShowDashboardTool.mention}: line chart from top5_q1 (series column "q1_oil"; the chart adapter detects the datapoint-array column and flattens to per-timestamp rows internally)`, */}
      {/*   ]} */}
      {/*   user="Plot Q1 production for the top 5 producing strings in Storm Eye" */}
      {/* /> */}
      {/**/}
      {/* <QueryExample */}
      {/*   name="production_for_well" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent view, filter name="A-01" -> get instanceId`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: FlatHierarchy_Agent view, filter well=instanceId -> get strings`, */}
      {/*     `IF multiple strings: ${AskUserQuestionTool.mention} with options populated from query results`, */}
      {/*     'IF only one string: proceed directly', */}
      {/*     `${QueryKnowledgeGraphTool.mention}: EstimatedRatesStringAgent, filter wellString=selected instanceId -> rates`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=rates, actions=[{type:"derive", derives:[{name:"oil_daily", expression:"daily(priorityOilRateStd)"}, {name:"gas_daily", expression:"daily(priorityGasRateStd)"}]}] -> rates_with_history (datapoint-array columns kept wide)`, */}
      {/*     `${ShowDashboardTool.mention}: production rate charts from rates_with_history (oil_daily and gas_daily as separate series; the chart adapter flattens datapoint arrays internally)`, */}
      {/*     `${AskUserQuestionTool.mention}: suggest next steps based on results — e.g., "Compare with another string", "View allocation data", "Check well status"`, */}
      {/*   ]} */}
      {/*   user="Show me the production for Well A-01" */}
      {/* /> */}
      {/**/}
      {/* <QueryExample */}
      {/*   name="open_ended_request" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent view -> get available entities and entity types`, */}
      {/*     `${AskUserQuestionTool.mention}: present filter options populated from actual data (e.g., fields, reservoirs, locations found in query)`, */}
      {/*     'User selects filter and entity', */}
      {/*     'Continue with standard flow for selected entity', */}
      {/*   ]} */}
      {/*   user="Show me wells" */}
      {/* /> */}
      {/**/}
      {/* <QueryExample */}
      {/*   name="comparison" */}
      {/*   steps={[ */}
      {/*     `${QueryKnowledgeGraphTool.mention}: Entity_Agent view -> resolve entity`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: FlatHierarchy_Agent -> get all string instanceIds in scope`, */}
      {/*     `${QueryKnowledgeGraphTool.mention}: EstimatedRatesString_Agent, filter wellString IN [instanceIds] -> rates`, */}
      {/*     `${TransformDatasetTool.mention}: inputDataset=rates, actions=[{type:"derive", derives:[{name:"oil_history", expression:"daily(priorityOilRateStd)"}]}] -> rates_with_history (datapoint-array column kept wide)`, */}
      {/*     `${ShowDashboardTool.mention}: comparison chart from rates_with_history (series column "oil_history"; one series per string after the chart adapter flattens internally)`, */}
      {/*   ]} */}
      {/*   user="Compare oil production across strings in Reservoir Y" */}
      {/* /> */}
    </Instructions>
  );
}
